import { Zap } from "lucide-react";
import { useDevAgentStore, type FeedItem } from "@/stores/devAgentStore";
import { PanelSection } from "./PanelSection";

function dedupeByClassName(items: FeedItem[]) {
  const seen = new Set<string>();
  return [...items]
    .reverse()
    .filter((item) => {
      const key = item.data?.className || item.data?.class_name || item.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .reverse();
}

function hasImpactData(data: Record<string, any>) {
  return Boolean(
    data.summary ||
      (Array.isArray(data.callers) && data.callers.length > 0) ||
      (typeof data.callers === "number" && data.callers > 0) ||
      (data.dependents != null && data.dependents > 0) ||
      (Array.isArray(data.data_access) && data.data_access.length > 0) ||
      (Array.isArray(data.triggers_at_risk) && data.triggers_at_risk.length > 0),
  );
}

function buildImpactMarkdown(data: Record<string, any>) {
  const lines = [`## Impact Analysis: ${data.className}`, ""];
  if (data.summary) lines.push(data.summary, "");
  if (data.callers?.length || data.dependents) {
    lines.push("| Type | Count |", "|---|---|");
    if (data.dependents != null) lines.push(`| Blast Radius | ${data.dependents} |`);
    if (data.callers != null) {
      const count = typeof data.callers === "number" ? data.callers : data.callers.length;
      lines.push(`| Callers | ${count} |`);
    }
  }
  if (Array.isArray(data.callers) && data.callers.length > 0) {
    lines.push("", "| Caller | Type | Risk |", "|---|---|---|");
    data.callers.forEach((caller: any) => {
      lines.push(`| ${caller.name} | ${caller.type} | ${caller.risk} |`);
    });
  }
  if (data.data_access?.length > 0) {
    lines.push("", "| Object | Operations |", "|---|---|");
    data.data_access.forEach((entry: any) => {
      lines.push(`| ${entry.object} | ${(entry.operations || []).join(", ")} |`);
    });
  }
  if (data.triggers_at_risk?.length > 0) {
    lines.push(
      "",
      `**Triggers at risk:** ${data.triggers_at_risk.map((trigger: any) => trigger.trigger).join(", ")}`,
    );
  }
  return lines.join("\n");
}

export function ImpactPanel({
  impactItems,
  analyzingFile,
  isBusy,
  onClear,
  onMoveUp,
  onMoveDown,
  onSendMessage,
  onOpenFileInEditor,
}: {
  impactItems: FeedItem[];
  analyzingFile?: string | null;
  isBusy?: boolean;
  onClear?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onSendMessage: (msg: string, displayText?: string) => void;
  onOpenFileInEditor: (file: { id: string; name: string; metadataType?: string }) => void;
}) {
  const uniqueItems = dedupeByClassName(impactItems);

  return (
    <PanelSection
      disabled={isBusy}
      title="Impact Analysis"
      icon={<Zap className="w-3.5 h-3.5" />}
      count={impactItems.length}
      onClear={onClear}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
    >
      {uniqueItems.length > 0 ? (
        <div className="space-y-1">
          {uniqueItems.map((item) => {
            const data = item.data;
            const clickable = hasImpactData(data)
              ? () => {
                  useDevAgentStore.getState().addFeedItem({
                    type: "chat_agent",
                    data: { content: buildImpactMarkdown(data) },
                  });
                }
              : undefined;
            return (
              <div
                key={item.id}
                className={`w-full text-left py-1.5 px-2 rounded transition-colors group ${clickable ? "hover:bg-muted/50 cursor-pointer" : "cursor-default opacity-80"}`}
                onClick={clickable}
              >
                {data.className && (
                  <div className="font-semibold text-xs text-foreground">
                    {data.className}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-0.5">
                  {data.dependents != null && (
                    <span className="text-[10px] text-muted-foreground font-medium">
                      {data.dependents} dependents
                    </span>
                  )}
                  {data.callers != null && (
                    <span className="text-[10px] text-muted-foreground font-medium">
                      {typeof data.callers === "number" ? data.callers : data.callers.length} callers
                    </span>
                  )}
                  {data.riskLevel && (
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                        data.riskLevel === "high"
                          ? "bg-red-500/25 text-red-800 border-red-600/40 dark:text-red-200 dark:border-red-400/50"
                          : data.riskLevel === "medium"
                            ? "bg-amber-500/25 text-amber-900 border-amber-600/40 dark:text-amber-200 dark:border-amber-400/50"
                            : "bg-emerald-500/25 text-emerald-900 border-emerald-600/40 dark:text-emerald-200 dark:border-emerald-400/50"
                      }`}
                    >
                      {data.riskLevel}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 mt-1.5">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSendMessage(`Show me the full dependency tree for ${data.className}`);
                    }}
                    className="text-[10px] font-medium text-blue-700 dark:text-blue-400 hover:underline hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    Dependencies
                  </button>
                  <span className="text-[10px] text-muted-foreground select-none" aria-hidden>
                    {"\u00B7"}
                  </span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSendMessage(`What would break if I change ${data.className}?`);
                    }}
                    className="text-[10px] font-medium text-blue-700 dark:text-blue-400 hover:underline hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    What breaks?
                  </button>
                  <span className="text-[10px] text-muted-foreground select-none" aria-hidden>
                    {"\u00B7"}
                  </span>
                  <button
                    type="button"
                    onClick={async (event) => {
                      event.stopPropagation();
                      let fileId = data.className;
                      try {
                        const { getMetadataItemByName } = await import("../../devAgentService");
                        const orgId = (await import("@/stores/authStore_v1")).useAuthStoreV1.getState().selectedOrg?.id;
                        const item = await getMetadataItemByName(data.className, orgId);
                        if (item?.id) fileId = String(item.id);
                      } catch {
                        /* fallback to class name */
                      }
                      onOpenFileInEditor({
                        id: fileId,
                        name: data.className,
                        metadataType: "ApexClass",
                      });
                    }}
                    className="text-[10px] font-medium text-blue-700 dark:text-blue-400 hover:underline hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    Open
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : analyzingFile ? (
        <div className="flex items-center gap-2 justify-center py-2 text-muted-foreground">
          <div className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-primary border-t-transparent" />
          <span className="text-xs font-medium">Analyzing {analyzingFile}...</span>
        </div>
      ) : (
        <div className="text-muted-foreground text-center py-2">
          No impact data yet
        </div>
      )}
    </PanelSection>
  );
}
